import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { api } from '../api';
import {
  NETWORKS,
  WITHDRAWAL_WHITELIST,
  WITHDRAWAL_WHITELIST_EXPRESS
} from '../../constants/withdrawalWhitelist';
import { CATALOGUE_PAGE_SIZE, collectCataloguePages } from './paging';
import {
  normalizeEntry,
  normalizeNetworkCatalogue,
  normalizeWhitelistPage,
  readExpressPreference
} from './normalize';
import {
  ExpressPreferenceBody,
  NetworkConfig,
  WhitelistAddBody,
  WhitelistDeleteBody,
  WhitelistEntry,
  WhitelistListParams,
  WhitelistPage
} from './types';

/** `networks` repeats rather than joining, so build the query string by hand. */
const listQueryString = ({
  networks,
  limit,
  offset
}: WhitelistListParams): string => {
  const params = new URLSearchParams();
  networks?.forEach((network) => params.append('networks', network));
  if (limit !== undefined) params.append('limit', String(limit));
  if (offset !== undefined) params.append('offset', String(offset));
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const withdrawalWhitelistSlice = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * The user's own destinations plus the saved express preference — read
     * both from the same response, since there is no settings GET.
     */
    getWhitelist: build.query<WhitelistPage, WhitelistListParams | void>({
      query: (params) =>
        `${WITHDRAWAL_WHITELIST}${listQueryString(params || {})}`,
      transformResponse: normalizeWhitelistPage,
      providesTags: ['WithdrawalWhitelist']
    }),

    /**
     * MFA-gated (WL template). The 499 challenge, the code and the replay are
     * handled globally by baseQueryWithReauth and the MFA container, so the
     * result of the replayed call arrives through setServiceIdResult rather
     * than this mutation — the caller has to invalidate the list itself.
     */
    addWhitelistEntry: build.mutation<WhitelistEntry, WhitelistAddBody>({
      query: (body) => ({
        url: WITHDRAWAL_WHITELIST,
        method: 'POST',
        body
      }),
      transformResponse: (response: any) => normalizeEntry(response?.data),
      invalidatesTags: ['WithdrawalWhitelist']
    }),

    /** Soft delete on the collection path — no `/{id}` suffix, no MFA. */
    deleteWhitelistEntry: build.mutation<{ id: string }, WhitelistDeleteBody>({
      query: ({ id }) => ({
        url: `${WITHDRAWAL_WHITELIST}?id=${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      transformResponse: (response: any) => ({
        id: response?.data?.id ?? ''
      }),
      invalidatesTags: ['WithdrawalWhitelist']
    }),

    /**
     * Both directions of the express preference, MFA-gated on the generic OTP
     * template. Same replay caveat as addWhitelistEntry.
     */
    setExpressPreference: build.mutation<boolean, ExpressPreferenceBody>({
      query: (body) => ({
        url: WITHDRAWAL_WHITELIST_EXPRESS,
        method: 'PUT',
        body
      }),
      transformResponse: (response: any) =>
        readExpressPreference(response?.data),
      invalidatesTags: ['WithdrawalWhitelist']
    }),

    /**
     * The network catalogue behind the add form. `/wallet/networks` paginates
     * for real, so read every page; the walk itself lives in ./paging.
     */
    getNetworkCatalogue: build.query<NetworkConfig[], void>({
      queryFn: async (_arg, _api, _extraOptions, fetchWithBQ) => {
        const { rows, error } = await collectCataloguePages(async (page) => {
          const response = await fetchWithBQ(
            `${NETWORKS}?page=${page}&pageLimit=${CATALOGUE_PAGE_SIZE}`
          );
          return response.error
            ? { error: response.error }
            : { rows: (response.data as any)?.data };
        });

        if (error) return { error: error as FetchBaseQueryError };
        return { data: normalizeNetworkCatalogue(rows) };
      }
    })
  }),
  overrideExisting: false
});

export const {
  useGetWhitelistQuery,
  useLazyGetWhitelistQuery,
  useAddWhitelistEntryMutation,
  useDeleteWhitelistEntryMutation,
  useSetExpressPreferenceMutation,
  useGetNetworkCatalogueQuery
} = withdrawalWhitelistSlice;
